import type { ApiErrorBody, ApiSuccess, FieldError, ListMeta, RetryInfo } from "@closer/shared";

export class ApiRequestError extends Error {
  constructor(
    public code: string,
    message: string,
    public fields?: FieldError[],
    public status?: number,
    /**
     * Yerelleştirme anahtarı (CLOS-306). Bugün ekranlarda `message` gösteriliyor;
     * çok dil devreye girdiğinde metin bu anahtardan üretilecek ve `message` yalnız
     * yedek kalacak. Anahtarı şimdiden taşımak, o gün her çağrı yerini gezmemek demek.
     */
    public messageKey?: string,
    /** Bu istek güvenle tekrarlanabilir mi, ne kadar sonra. */
    public retry?: RetryInfo,
  ) {
    super(message);
    this.name = "ApiRequestError";
  }
}

/**
 * Tek HTTP giriş noktası (S10). Zarfı çözer; ok:false ise ApiRequestError fırlatır.
 * Kural: elle fetch/axios YASAK — her çağrı buradan geçer.
 */
export async function api<T>(
  path: string,
  init?: RequestInit & { tenantId?: string },
): Promise<{ data: T; meta?: ListMeta }> {
  const { tenantId, headers, ...rest } = init ?? {};

  const response = await fetch(path, {
    credentials: "include",
    ...rest,
    headers: {
      ...(rest.body !== undefined && rest.body !== null
        ? { "Content-Type": "application/json" }
        : {}),
      ...(tenantId ? { "x-tenant-id": tenantId } : {}),
      ...(headers as Record<string, string> | undefined),
    },
  });

  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    throw new ApiRequestError(
      "INTERNAL",
      "Sunucudan beklenmeyen bir yanıt alındı. Lütfen tekrar deneyin.",
      undefined,
      response.status,
    );
  }

  const envelope = payload as ApiSuccess<T> | ApiErrorBody;
  if (!envelope.ok) {
    throw new ApiRequestError(
      envelope.error.code,
      envelope.error.message,
      envelope.error.fields,
      response.status,
      envelope.error.messageKey,
      envelope.error.retry,
    );
  }
  return { data: envelope.data, meta: envelope.meta };
}
